import { useCallback, useEffect, useRef, useState } from "react";

// Like useCachedFetch, but for "Load more"-style paginated lists: items
// accumulate across loadMore() calls instead of being replaced. A failed
// loadMore() preserves already-loaded items (only sets `error`) so a user
// never loses visible data from a bad "load more" click. Deliberately
// uncached — always fetches fresh on mount — so a list never goes stale for
// the life of a browser session; request-volume protection is left to the
// server's rate limiter rather than duplicated here.
export function usePaginatedFetch(fetchPage, { key, enabled = true, pageSize = 10, maxItems } = {}) {
  const [items, setItems] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(enabled);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [retryTick, setRetryTick] = useState(0);
  const fetchPageRef = useRef(fetchPage);
  fetchPageRef.current = fetchPage;
  const itemsRef = useRef(items);
  itemsRef.current = items;
  const loadMoreControllerRef = useRef(null);

  useEffect(() => {
    return () => loadMoreControllerRef.current?.abort();
  }, []);

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return;
    }

    const controller = new AbortController();
    setLoading(true);
    setError(null);
    fetchPageRef.current(0, pageSize, controller.signal)
      .then((result) => {
        if (controller.signal.aborted) return;
        const capped = maxItems && result.items.length > maxItems;
        setItems(capped ? result.items.slice(0, maxItems) : result.items);
        setHasMore(capped ? false : result.hasMore);
      })
      .catch((err) => { if (!controller.signal.aborted) setError(err); })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
    // fetchPage is intentionally excluded — captured via ref so callers don't
    // need to memoize it themselves.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, enabled, retryTick, pageSize, maxItems]);

  const loadMore = useCallback(() => {
    if (!hasMore || loadingMore) return;
    const limit = maxItems ? Math.min(pageSize, maxItems - itemsRef.current.length) : pageSize;
    const controller = new AbortController();
    loadMoreControllerRef.current = controller;
    setLoadingMore(true);
    setError(null);
    fetchPageRef.current(itemsRef.current.length, limit, controller.signal)
      .then((result) => {
        if (controller.signal.aborted) return;
        const combinedLength = itemsRef.current.length + result.items.length;
        const capped = maxItems && combinedLength >= maxItems;
        setItems((prev) => {
          const next = [...prev, ...result.items];
          return maxItems ? next.slice(0, maxItems) : next;
        });
        setHasMore(capped ? false : result.hasMore);
      })
      .catch((err) => { if (!controller.signal.aborted) setError(err); })
      .finally(() => { if (!controller.signal.aborted) setLoadingMore(false); });
  }, [hasMore, loadingMore, pageSize, maxItems]);

  const retry = useCallback(() => {
    setItems([]);
    setHasMore(false);
    setRetryTick((t) => t + 1);
  }, []);

  return { items, hasMore, loading, loadingMore, error, loadMore, retry };
}
